import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface Option {
  id: string;
  name: string;
  logo?: string;
}

interface SearchableDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  className?: string;
  loading?: boolean;
  disabled?: boolean;
  showClearIcon?: boolean; // Control whether to show clear icon
  onClear?: () => void; // Custom clear handler
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select option",
  className = "",
  loading = false,
  disabled = false,
  showClearIcon = true, // Default to true for backward compatibility
  onClear
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownCoords, setDropdownCoords] = useState<{ top: number; left: number; width: number } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find(option => option.id === value);

  const filteredOptions = options.filter(option =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate dropdown position (viewport-relative for fixed positioning in portal).
  // Prefer below the input so the list feels attached to the field; only position above when there's almost no room below.
  const calculateDropdownPosition = React.useCallback(() => {
    if (!inputRef.current) return;

    const inputRect = inputRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 192; // max-h-48 = 12rem = 192px
    const gap = 4;
    const minSpaceBelowToPreferBelow = 80; // Prefer below unless less than this (px)

    const spaceBelow = viewportHeight - inputRect.bottom;
    const spaceAbove = inputRect.top;

    let top: number;
    if (spaceBelow < minSpaceBelowToPreferBelow && spaceAbove > dropdownHeight) {
      // Only then: show above the input
      top = inputRect.top - dropdownHeight - gap;
    } else {
      // Default: show directly below the input (dropdown scrolls if needed)
      top = inputRect.bottom + gap;
    }

    setDropdownCoords({
      top,
      left: inputRect.left,
      width: inputRect.width,
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        inputRef.current &&
        !inputRef.current.contains(target)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Position dropdown when open; recalc when search term changes so it stays under the input
  useEffect(() => {
    if (!isOpen) return;
    const run = () => {
      requestAnimationFrame(() => {
        calculateDropdownPosition();
      });
    };
    run();
  }, [isOpen, searchTerm, calculateDropdownPosition]);

  // Keep dropdown stuck to input on scroll (window + scrollable parents)
  useEffect(() => {
    if (!isOpen || !inputRef.current) return;

    const scrollParent = (el: HTMLElement | null): HTMLElement | null => {
      if (!el) return null;
      const { overflow, overflowY } = getComputedStyle(el);
      const scrollable = /auto|scroll|overlay/.test(overflow + overflowY);
      if (scrollable && el.scrollHeight > el.clientHeight) return el;
      return scrollParent(el.parentElement);
    };

    const container = scrollParent(inputRef.current);
    const handleScroll = () => {
      requestAnimationFrame(calculateDropdownPosition);
    };

    window.addEventListener('scroll', handleScroll, true);
    container?.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      container?.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, calculateDropdownPosition]);

  const handleInputClick = () => {
    if (disabled) return;
    setIsOpen(true);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleOptionSelect = (option: Option) => {
    onChange(option.id);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClearSelection = () => {
    if (disabled) return;
    if (onClear) {
      onClear();
    } else {
      onChange('');
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  // Prevent dropdown from opening when disabled
  useEffect(() => {
    if (disabled && isOpen) {
      setIsOpen(false);
    }
  }, [disabled, isOpen]);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : (selectedOption?.name || '')}
          onClick={handleInputClick}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full text-sm text-gray-900 border rounded px-3 py-2 pr-8 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
            disabled 
              ? 'bg-gray-100 cursor-not-allowed border-gray-200' 
              : 'bg-white border-gray-200'
          }`}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
          {selectedOption && showClearIcon && (
            <svg 
              className="w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              onClick={handleClearSelection}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
        </div>
      </div>
      
      {isOpen && dropdownCoords && createPortal(
        <div 
          ref={dropdownRef}
          className="fixed z-[9999] bg-white border border-gray-200 rounded shadow-lg max-h-48 overflow-y-auto"
          style={{
            top: `${dropdownCoords.top}px`,
            left: `${dropdownCoords.left}px`,
            width: `${dropdownCoords.width}px`,
          }}
        >
          {loading ? (
            <div className="p-2 text-xs text-gray-500 text-center">Loading...</div>
          ) : filteredOptions.length === 0 ? (
            <div className="p-2 text-xs text-gray-500 text-center">No options found</div>
          ) : (
            filteredOptions.map((option) => (
              <div
                key={option.id}
                onClick={() => handleOptionSelect(option)}
                className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center space-x-2"
              >
                {option.logo && (
                  <img 
                    src={option.logo} 
                    alt={option.name}
                    className="w-4 h-4 rounded object-cover"
                    onError={(e) => {
                      e.currentTarget.src = 'https://via.placeholder.com/16x16/cccccc/666666?text=🏟️';
                    }}
                  />
                )}
                <span>{option.name}</span>
              </div>
            ))
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default SearchableDropdown;
